import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, Download } from "lucide-react";
import IconClose from "@/assets/icons/close.svg?react";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer.tsx";
import { AccessDeniedError } from "@telegram-apps/sdk-react";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import { getApiErrorMessage } from "@/shared/utils/getApiErrorMessage.ts";
import {
  downloadReceiptToDevice,
  shareReceiptToContact,
} from "@/shared/utils/receiptPdf.ts";

/** True when the thrown error is the user backing out of a Telegram
 *  download/share popup (Cancel tap) rather than a real failure.
 *  Telegram's `downloadFile` rejects with AccessDeniedError; native
 *  share / fetch aborts reject with AbortError. Neither warrants an
 *  error toast — the user made a deliberate choice. */
function isUserCancellation(e: unknown): boolean {
  return (
    e instanceof AccessDeniedError ||
    (e instanceof Error && e.name === "AbortError")
  );
}

interface ShareChekSheetProps {
  isOpen: boolean;
  onClose: () => void;
  transferId: string | null;
}

/* ---- Single action row ---- */
function ActionRow({
  icon,
  label,
  description,
  onClick,
  loading,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
  loading: boolean;
}) {
  return (
    <VibratingButton
      onClick={onClick}
      vibrationType="selection"
      disabled={loading}
      className="w-full p-4 flex items-center gap-3 text-left rounded-2xl bg-white/[0.04] border border-white/[0.06] active:bg-white/[0.07] transition-colors disabled:opacity-50"
    >
      <div className="size-10 rounded-full bg-orange-100/12 flex-center shrink-0 text-orange-100">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white leading-130">{label}</p>
        <p className="text-xs text-gray-1 leading-130 mt-0.5">
          {description}
        </p>
      </div>
    </VibratingButton>
  );
}

/* ---- Sheet ---- */

export default function ShareChekSheet({
  isOpen,
  onClose,
  transferId,
}: ShareChekSheetProps) {
  const { t } = useTranslation();
  // Track which action is in-flight so the rows show a disabled
  // state and we can ignore double-taps. Single state is enough —
  // only one action runs at a time.
  const [busy, setBusy] = useState<"share" | "save" | null>(null);

  // Generic runner: gates double-clicks, surfaces a localized error
  // message from the helper, closes the sheet on success. `successToast`
  // is resolved from the action's result so the share action can toast
  // only on the Android bot-drop fallback (where the chek lands in the
  // user's own chat, not the contact's).
  const run = async <R,>(
    kind: "share" | "save",
    fn: () => Promise<R>,
    opts: {
      errorFallbackKey: string;
      errorFallbackText: string;
      successToast?: (
        result: R
      ) => { text: string; variant: "success" } | undefined;
    }
  ) => {
    if (!transferId || busy) return;
    setBusy(kind);
    try {
      const result = await fn();
      const toast = opts.successToast?.(result);
      if (toast) {
        showCustomToast(toast.text, toast.variant);
      }
      onClose();
    } catch (e: unknown) {
      // User tapped Cancel on the Telegram download/share popup — not a
      // failure, so stay silent (no error toast). Sheet stays open so
      // they can pick another channel or close it themselves.
      if (isUserCancellation(e)) {
        return;
      }
      console.error(`share action [${kind}] failed:`, e);
      showCustomToast(
        getApiErrorMessage(
          e,
          t(opts.errorFallbackKey) || opts.errorFallbackText
        ),
        "error"
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={(e) => !e && onClose()}>
      <DrawerContent
        className="bg-gray-4 max-h-[85vh] z-[10000]"
        overlayClassName="z-[10000]"
        showTopThumb
      >
        <DrawerHeader className="flex justify-between items-center mb-1">
          <DrawerTitle className="text-lg">
            {t("p2p_share_chek_title") || "Chekni qanday ulashmoqchisiz?"}
          </DrawerTitle>
          <DrawerClose>
            <IconClose className="size-6 text-gray-1 cursor-pointer" />
          </DrawerClose>
        </DrawerHeader>
        <DrawerBody>
          <div className="flex flex-col gap-2 pb-4">
            <ActionRow
              icon={<Send className="size-5" strokeWidth={2.2} />}
              label={
                t("p2p_share_via_telegram") || "Telegram kontaktga yuborish"
              }
              description={
                t("p2p_share_via_telegram_desc") ||
                "Kontakt tanlanadi va chek fayli yuboriladi"
              }
              loading={busy === "share"}
              onClick={() =>
                run("share", () => shareReceiptToContact(transferId!), {
                  errorFallbackKey: "p2p_share_failed",
                  errorFallbackText: "Chek yuborilmadi",
                  // No success toast — both the document and link paths
                  // hand off to Telegram's own contact-picker UI.
                })
              }
            />
            <ActionRow
              icon={<Download className="size-5" strokeWidth={2.2} />}
              label={t("p2p_save_to_phone") || "Telefonga saqlash"}
              description={
                t("p2p_save_to_phone_desc") ||
                "Chek PDF fayl sifatida yuklab olinadi"
              }
              loading={busy === "save"}
              onClick={() =>
                run("save", () => downloadReceiptToDevice(transferId!), {
                  errorFallbackKey: "p2p_receipt_save_failed",
                  errorFallbackText: "Chekni yuklab bo'lmadi",
                  successToast: () => ({
                    text: t("p2p_receipt_downloaded") || "Chek yuklab olindi",
                    variant: "success",
                  }),
                })
              }
            />
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
